import type { BriefMethod } from '@sigora/domain';

import {
  BRIEF_HEADINGS,
  LIMITATION_HEADINGS,
  SIMULATED_LINE,
  completesLine,
  fieldedLine,
  formatLine,
  versionsLine,
} from '../brief-copy.ts';
import { type RevisionMarks, UpdatedTag } from './RevisionMarks.tsx';
import { briefDate } from './brief-date.ts';

/**
 * Method and audience: who finished out of how many were ordered, what the conversation was,
 * when it ran, who it was for, the accepted versions, and the Limitations word for word in the
 * order the audience stores them. Every fact is the Brief's own. `marks` the block a Sigora
 * revision changed.
 */
export const MethodBlock = ({
  method,
  marks,
}: {
  method: BriefMethod;
  marks?: RevisionMarks | null;
}) => {
  const { launched_at: launched, ended_at: ended } = method.fielded;
  const { targeting_gaps: gaps, pipeline_constraints: constraints } = method.limitations;

  return (
    <>
      <h2>
        {BRIEF_HEADINGS.method}
        <UpdatedTag marks={marks} block="method" />
      </h2>
      <p data-test-id="brief-method-completes">
        {completesLine(method.completes, method.ordered_n)}
      </p>
      <p data-test-id="brief-method-format">
        {formatLine(method.format.open, method.format.polls, method.minutes)}
      </p>
      {launched !== null && ended !== null && (
        <p data-test-id="brief-method-fielded">
          {fieldedLine(briefDate(launched), briefDate(ended))}
        </p>
      )}
      <p data-test-id="brief-method-audience">{method.audience.description}</p>
      <p data-test-id="brief-method-countries">{method.audience.countries.join(', ')}</p>
      {method.simulated && <p data-test-id="brief-method-simulated">{SIMULATED_LINE}</p>}
      <p className="brief-meta" data-test-id="brief-method-versions">
        {versionsLine(method.versions)}
      </p>

      {gaps.length > 0 && (
        <>
          <h3>{LIMITATION_HEADINGS.targetingGaps}</h3>
          <ul className="brief-list">
            {gaps.map((gap) => (
              <li key={gap.id} data-test-id="brief-limitation" data-limitation-kind="targeting_gap">
                {gap.text}
              </li>
            ))}
          </ul>
        </>
      )}
      {constraints.length > 0 && (
        <>
          <h3>{LIMITATION_HEADINGS.pipelineConstraints}</h3>
          <ul className="brief-list">
            {constraints.map((constraint) => (
              <li
                key={constraint.key}
                data-test-id="brief-limitation"
                data-limitation-kind="pipeline_constraint"
              >
                {constraint.text}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
};
